import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

export const MESI = ['','Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
                     'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

export const eur = n => '\u20ac ' + parseFloat(n || 0).toLocaleString('it-IT',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtData = d => d
  ? String(d).substring(0, 10).split('-').reverse().join('/')
  : '\u2014';

const BADGE = {
  bozza:      { t: 'Bozza',      bg: '#f1f5f9', c: '#475569' },
  emesso:     { t: 'Emesso',     bg: '#dbeafe', c: '#1e40af' },
  accettato:  { t: 'Accettato',  bg: '#dcfce7', c: '#166534' },
  rifiutato:  { t: 'Rifiutato',  bg: '#fee2e2', c: '#991b1b' },
  convertito: { t: 'Convertito', bg: '#ede9fe', c: '#5b21b6' },
  annullato:  { t: 'Annullato',  bg: '#f1f5f9', c: '#94a3b8' },
};

export function Badge({ stato }) {
  const b = BADGE[stato] || BADGE.bozza;
  return (
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12,
                   background: b.bg, color: b.c, fontWeight: 600 }}>
      {b.t}
    </span>
  );
}

function ordina(dati, col, dir) {
  if (!col) return dati;
  return [...(dati || [])].sort((a, b) => {
    let va = a[col], vb = b[col];
    if (va == null) va = '';
    if (vb == null) vb = '';
    const na = parseFloat(va), nb = parseFloat(vb);
    if (!isNaN(na) && !isNaN(nb) && String(va).trim() !== '' && String(vb).trim() !== '') {
      va = na; vb = nb;
    } else {
      va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
    }
    if (va === vb) return 0;
    return dir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });
}

function useSortState(colDefault, dirDefault) {
  const [sortCol, setCol] = useState(colDefault);
  const [sortDir, setDir] = useState(dirDefault || 'desc');
  const onSort = col => {
    if (col === sortCol) setDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setCol(col); setDir('asc'); }
  };
  return { sortCol, sortDir, onSort };
}

function SortTh({ col, label, align, width, sortCol, sortDir, onSort }) {
  const attivo = sortCol === col;
  return (
    <th onClick={() => onSort(col)} style={{
      padding: '8px 10px', background: '#f1f5f9', borderBottom: '2px solid #e5e7eb',
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      textAlign: align || 'left', width, cursor: 'pointer', userSelect: 'none',
    }}>
      {label}{' '}
      <span style={{ color: attivo ? '#b30000' : '#ccc', fontSize: 10 }}>
        {attivo ? (sortDir === 'asc' ? '\u25b2' : '\u25bc') : '\u21c5'}
      </span>
    </th>
  );
}

export default function DocumentiPage({ tipo = 'preventivo' }) {
  const [vista, setVista] = useState('lista');
  const [selId, setSelId] = useState(null);

  const apri  = id => { setSelId(id); setVista('dettaglio'); };
  const nuovo = () => { setSelId(null); setVista('editor'); };
  const modifica = id => { setSelId(id); setVista('editor'); };
  const torna = () => { setVista('lista'); };

  if (vista === 'dettaglio')
    return <DettaglioDocumento id={selId} onBack={torna} onModifica={modifica} />;
  if (vista === 'editor')
    return <EditorDocumento id={selId} tipo={tipo} onBack={torna}
                            onSalvato={id => apri(id)} />;

  return <ListaDocumenti tipo={tipo} onApri={apri} onNuovo={nuovo} />;
}

function ListaDocumenti({ tipo, onApri, onNuovo }) {
  const [righe, setRighe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [stato, setStato] = useState('');
  const [selezione, setSel] = useState([]);   // per la conversione
  const { sortCol, sortDir, onSort } = useSortState('numero', 'desc');
  const [tecnici, setTecnici] = useState([]);
  const [assegn, setAssegn]   = useState({});   // documentoId -> tecnicoId
  const [busy, setBusy] = useState(false);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ tipo });
      if (stato) p.set('stato', stato);
      if (q) p.set('q', q);
      const r = await api.get(`/documenti?${p.toString()}`);
      setRighe(r.data?.data || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [tipo, stato, q]);

  useEffect(() => {
    const t = setTimeout(carica, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [carica, q]);

  useEffect(() => {
    if (tipo !== 'ddt') return;
    Promise.all([
      api.get('/utenti').catch(() => ({ data: { data: [] } })),
      api.get('/ddt-assegnazioni').catch(() => ({ data: { data: [] } })),
    ]).then(([ru, ra]) => {
      const lista = ru.data?.data || ru.data || [];
      setTecnici(lista.filter(u => (u.ruolo || '').toLowerCase() === 'tecnico'));
      const m = {};
      (ra.data?.data || []).forEach(x => {
        if (x.documentoId) m[x.documentoId] = x.tecnicoId;
      });
      setAssegn(m);
    });
  }, [tipo]);

  const assegnaTecnico = async (docId, tecnicoId) => {
    const prec = assegn[docId] ?? null;
    const val = tecnicoId ? parseInt(tecnicoId, 10) : null;
    setAssegn(a => ({ ...a, [docId]: val }));
    try {
      await api.post(`/ddt-assegnazioni/documento/${docId}`, { tecnicoId: val });
    } catch (e) {
      setAssegn(a => ({ ...a, [docId]: prec }));
      window.alert(e?.response?.data?.error || 'Impossibile assegnare il DDT.');
    }
  };

  const scaricaPdf = async (d) => {
    try {
      const res = await api.get(`/documenti/${d.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tipo === 'ddt' ? 'DDT' : 'Preventivo'}_${d.numero}_${d.anno}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert('Errore nel download del PDF.');
    }
  };

  const converti = async () => {
    if (selezione.length === 0) return;
    if (!window.confirm(`Convertire ${selezione.length} preventivi in un DDT?`)) return;
    setBusy(true);
    try {
      const r = await api.post('/documenti/converti', { ids: selezione });
      window.alert('DDT creato in bozza. Lo trovi nella sezione DDT.');
      setSel([]);
      carica();
    } catch (e) {
      window.alert('Errore: ' + (e?.response?.data?.error || e.message));
    } finally { setBusy(false); }
  };

  const ordinate = ordina(righe, sortCol, sortDir);

  const fatturaSel = async () => {
    if (selezione.length === 0) return;
    try {
      const ant = await api.post('/documenti/fattura',
        { ids: selezione, anteprima: true });
      const a = ant.data || {};
      const riepilogo =
        `Cliente: ${a.cliente}\n` +
        `Documenti: ${(a.documenti || []).map(x => x.numero).join(', ')}\n` +
        `Imponibile: ${eur(a.imponibile)}\n` +
        (a.ritenuta
          ? `Ritenuta 4%: ${eur(a.importoRitenuta)} su ${eur(a.baseRitenuta)} di manutenzioni\n`
          : 'Nessuna ritenuta d\'acconto\n');

      if (!window.confirm(riepilogo + '\nCreare la fattura su Fatture in Cloud?')) return;

      setBusy(true);
      const r = await api.post('/documenti/fattura', { ids: selezione });
      window.alert(`Fattura ${r.data?.numero || ''} creata su Fatture in Cloud.`);
      setSel([]);
      carica();
    } catch (e) {
      window.alert('Errore: ' + (e?.response?.data?.error || e.message));
    } finally { setBusy(false); }
  };

  const toggleSel = id =>
    setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const th = { padding: '8px 10px', background: '#f1f5f9', fontSize: 11,
               fontWeight: 600, textAlign: 'left', borderBottom: '2px solid #e5e7eb' };

  return (
    <div className="page-content">
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap',
                    alignItems: 'center' }}>
        <input className="form-control" style={{ maxWidth: 260 }}
          placeholder="Cerca cliente o oggetto..."
          value={q} onChange={e => setQ(e.target.value)} />

        <select className="form-control" style={{ maxWidth: 160, fontSize: 13 }}
          value={stato} onChange={e => setStato(e.target.value)}>
          <option value="">Tutti gli stati</option>
          {Object.keys(BADGE).map(k =>
            <option key={k} value={k}>{BADGE[k].t}</option>)}
        </select>

        <button className="btn btn-primary" onClick={onNuovo}>
          Nuovo {tipo === 'ddt' ? 'DDT' : 'preventivo'}
        </button>

        {tipo === 'preventivo' && selezione.length > 0 && (
          <button className="btn btn-secondary" onClick={converti} disabled={busy}>
            Converti {selezione.length} in DDT
          </button>
        )}

        {selezione.length > 0 && (
          <button className="btn btn-secondary" onClick={fatturaSel} disabled={busy}>
            Fattura {selezione.length} {selezione.length > 1 ? 'documenti' : 'documento'}
          </button>
        )}

        <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 13 }}>
          {righe.length} documenti
        </span>
      </div>

      {loading ? <div className="loading"><div className="spinner"></div></div> : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table className="q-table">
              <thead><tr>
                <th style={{ ...th, width: 34 }}></th>
                <SortTh col="numero" label="N." width={70}
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <SortTh col="dataDocumento" label="Data" width={90}
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <SortTh col="clienteNome" label="Cliente"
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <SortTh col="oggetto" label="Oggetto"
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <SortTh col="totale" label="Totale" align="right" width={100}
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <SortTh col="stato" label="Stato" width={100}
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                {tipo === 'ddt' && <th style={{ ...th, width: 130 }}>Tecnico</th>}
                <th style={{ ...th, width: 90 }}></th>
              </tr></thead>
              <tbody>
                {righe.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: '#9ca3af',
                                               padding: 32 }}>
                    Nessun documento
                  </td></tr>
                )}
                {ordinate.map(d => (
                  <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => onApri(d.id)}>
                    <td onClick={e => e.stopPropagation()}>
                      {(d.stato === 'accettato' || d.stato === 'emesso') && (
                        <input type="checkbox" checked={selezione.includes(d.id)}
                          onChange={() => toggleSel(d.id)}
                          style={{ width: 15, height: 15, cursor: 'pointer' }} />
                      )}
                    </td>
                    <td><strong>{d.numero || '\u2014'}</strong>
                        <span style={{ color: '#9ca3af', fontSize: 11 }}>/{d.anno}</span></td>
                    <td style={{ fontSize: 12 }}>{fmtData(d.dataDocumento)}</td>
                    <td><strong>{d.clienteNome}</strong>
                        {d.sedeOperativa && (
                          <div style={{ fontSize: 11, color: '#6b7280' }}>{d.sedeOperativa}</div>
                        )}</td>
                    <td style={{ fontSize: 12, color: '#6b7280', maxWidth: 240,
                                 overflow: 'hidden', textOverflow: 'ellipsis',
                                 whiteSpace: 'nowrap' }}>{d.oggetto || '\u2014'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur(d.totale)}</td>
                    <td><Badge stato={d.stato} /></td>
                    {tipo === 'ddt' && (
                      <td onClick={e => e.stopPropagation()}>
                        {d.stato !== 'bozza' && (
                          <select value={assegn[d.id] || ''}
                            onChange={e => assegnaTecnico(d.id, e.target.value)}
                            style={{ fontSize: 12, padding: '3px 6px', maxWidth: 125,
                                     border: '1px solid #e5e7eb', borderRadius: 6,
                                     color: assegn[d.id] ? '#111827' : '#9ca3af' }}>
                            <option value="">&mdash; nessuno &mdash;</option>
                            {tecnici.map(t => (
                              <option key={t.id} value={t.id}>{t.nome}</option>
                            ))}
                          </select>
                        )}
                      </td>
                    )}
                    <td onClick={e => e.stopPropagation()}>
                      {d.stato !== 'bozza' && (
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => scaricaPdf(d)}>PDF</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function DettaglioDocumento({ id, onBack, onModifica }) {
  const [doc, setDoc] = useState(null);
  const [righe, setRighe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/documenti/${id}`);
      setDoc(r.data?.data?.documento || null);
      setRighe(r.data?.data?.righe || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  const azione = async (fn, conferma) => {
    if (conferma && !window.confirm(conferma)) return;
    setBusy(true);
    try {
      await fn();
      await carica();
    } catch (e) {
      window.alert('Errore: ' + (e?.response?.data?.error || e.message));
    } finally { setBusy(false); }
  };

  const emetti = () => azione(
    () => api.post(`/documenti/${id}/emetti`, {}),
    'Emettere il documento? Verra\' assegnato il numero definitivo e non sara\' piu\' una bozza.');

  const cambiaStato = (stato, testo) => azione(
    () => api.post(`/documenti/${id}/stato`, { stato }), testo);

  const elimina = () => azione(async () => {
    await api.delete(`/documenti/${id}`);
    onBack();
  }, 'Eliminare definitivamente questa bozza?');

  const scaricaPdf = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/documenti/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.tipo === 'ddt' ? 'DDT' : 'Preventivo'}_${doc.numero}_${doc.anno}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert('Errore nel download del PDF.');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!doc) return null;

  const isBozza = doc.stato === 'bozza';
  const chiuso = doc.stato === 'convertito' || doc.stato === 'annullato' || doc.ficFatturaId;
  const th = { padding: '8px 10px', background: '#f1f5f9', fontSize: 11,
               fontWeight: 600, textAlign: 'left' };

  return (
    <div className="page-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
                    flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={onBack}>Indietro</button>
        <h2 style={{ margin: 0, fontSize: 18 }}>
          {doc.tipo === 'ddt' ? 'DDT' : 'Preventivo'} n. {doc.numero || '\u2014'}/{doc.anno}
        </h2>
        <Badge stato={doc.stato} />
        {doc.ficFatturaNum && (
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            Fattura {doc.ficFatturaNum}
          </span>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {!chiuso && (
            <button className="btn btn-secondary btn-sm" disabled={busy}
              onClick={() => onModifica(id)}>Modifica</button>
          )}
          {isBozza && (
            <button className="btn btn-primary btn-sm" disabled={busy}
              onClick={emetti}>Emetti</button>
          )}
          {isBozza && (
            <button className="btn btn-secondary btn-sm" disabled={busy}
              style={{ color: '#dc2626', borderColor: '#fecaca' }}
              onClick={elimina}>Elimina</button>
          )}
          {doc.stato === 'emesso' && doc.tipo === 'preventivo' && (
            <>
              <button className="btn btn-secondary btn-sm" disabled={busy}
                onClick={() => cambiaStato('accettato', 'Segnare il preventivo come accettato?')}>
                Accettato</button>
              <button className="btn btn-secondary btn-sm" disabled={busy}
                onClick={() => cambiaStato('rifiutato', 'Segnare il preventivo come rifiutato?')}>
                Rifiutato</button>
            </>
          )}
          {!isBozza && !chiuso && (
            <button className="btn btn-secondary btn-sm" disabled={busy}
              style={{ color: '#dc2626', borderColor: '#fecaca' }}
              onClick={() => cambiaStato('annullato',
                'Annullare il documento? Il numero resta assegnato e il documento ' +
                'non sara\' piu\' utilizzabile.')}>
              Annulla documento</button>
          )}
          {!isBozza && (
            <button className="btn btn-primary btn-sm" disabled={busy}
              onClick={scaricaPdf}>Scarica PDF</button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>CLIENTE</div>
            <div style={{ fontWeight: 600 }}>{doc.clienteNome}</div>
            {doc.indirizzo && <div style={{ fontSize: 13 }}>{doc.indirizzo}</div>}
            <div style={{ fontSize: 13 }}>
              {[doc.cap, doc.citta, doc.provincia && `(${doc.provincia})`]
                .filter(Boolean).join(' ')}
            </div>
            {doc.clientePiva && (
              <div style={{ fontSize: 12, color: '#6b7280' }}>P.IVA {doc.clientePiva}</div>
            )}
            {!doc.clientePiva && doc.clienteCf && (
              <div style={{ fontSize: 12, color: '#6b7280' }}>C.F. {doc.clienteCf}</div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>DOCUMENTO</div>
            <div style={{ fontSize: 13 }}>Data: {fmtData(doc.dataDocumento)}</div>
            {doc.sedeOperativa && (
              <div style={{ fontSize: 13 }}>Sede: {doc.sedeOperativa}</div>
            )}
            {doc.oggetto && (
              <div style={{ fontSize: 13 }}>Oggetto: {doc.oggetto}</div>
            )}
            {doc.causale && (
              <div style={{ fontSize: 13 }}>Causale: {doc.causale}</div>
            )}
          </div>
        </div>
        {doc.note && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#6b7280' }}>{doc.note}</div>
        )}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table className="q-table">
            <thead><tr>
              <th style={{ ...th, width: 90 }}>Codice</th>
              <th style={th}>Descrizione</th>
              <th style={{ ...th, textAlign: 'right', width: 70 }}>Q.ta</th>
              <th style={{ ...th, textAlign: 'right', width: 90 }}>Prezzo</th>
              <th style={{ ...th, textAlign: 'right', width: 60 }}>Sc.%</th>
              <th style={{ ...th, textAlign: 'right', width: 100 }}>Importo</th>
              <th style={{ ...th, width: 40, textAlign: 'center' }}>R.A.</th>
            </tr></thead>
            <tbody>
              {righe.map(r => (
                <tr key={r.id}>
                  <td style={{ fontSize: 12, color: '#6b7280' }}>{r.codice || ''}</td>
                  <td style={{ fontSize: 13 }}>
                    {r.descrizione}
                    {r.dettaglio && (
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.dettaglio}</div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', fontSize: 13 }}>
                    {parseFloat(r.quantita)} {r.udm || ''}
                  </td>
                  <td style={{ textAlign: 'right', fontSize: 13 }}>{eur(r.prezzoUnitario)}</td>
                  <td style={{ textAlign: 'right', fontSize: 13 }}>
                    {parseFloat(r.scontoPerc) ? parseFloat(r.scontoPerc) + '%' : ''}
                  </td>
                  <td style={{ textAlign: 'right', fontSize: 13, fontWeight: 600 }}>
                    {eur(r.imponibile)}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12 }}>
                    {r.soggettaRitenuta ? 'si' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ padding: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <table style={{ fontSize: 13 }}>
            <tbody>
              <tr><td style={{ padding: '2px 16px 2px 0' }}>Imponibile</td>
                  <td style={{ textAlign: 'right' }}>{eur(doc.imponibile)}</td></tr>
              <tr><td style={{ padding: '2px 16px 2px 0' }}>IVA</td>
                  <td style={{ textAlign: 'right' }}>{eur(doc.iva)}</td></tr>
              <tr><td style={{ padding: '6px 16px 2px 0', fontWeight: 700,
                               borderTop: '1px solid #e5e7eb' }}>TOTALE</td>
                  <td style={{ textAlign: 'right', fontWeight: 700,
                               borderTop: '1px solid #e5e7eb' }}>{eur(doc.totale)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


const RIGA_VUOTA = {
  prodottoId: null, codice: '', descrizione: '', dettaglio: '', udm: '',
  quantita: 1, prezzoUnitario: 0, scontoPerc: 0, aliquotaIva: 0.22,
  soggettaRitenuta: false,
};

function EditorDocumento({ id, tipo, onBack, onSalvato }) {
  const [clienti, setClienti] = useState([]);
  const [sedi, setSedi] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [cercaProd, setCercaProd] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tipoDoc, setTipoDoc] = useState(tipo);

  const [form, setForm] = useState({
    clienteId: '', dataDocumento: new Date().toISOString().slice(0, 10),
    sedeOperativa: '', oggetto: '', causale: '', note: '',
  });
  const [righe, setRighe] = useState([{ ...RIGA_VUOTA }]);

  useEffect(() => {
    (async () => {
      try {
        const rc = await api.get('/clienti');
        setClienti(rc.data?.data || []);
        if (id) {
          const rd = await api.get(`/documenti/${id}`);
          const d = rd.data?.data?.documento;
          const rr = rd.data?.data?.righe || [];
          if (d) {
            setTipoDoc(d.tipo);
            setForm({
              clienteId: d.clienteId || '',
              dataDocumento: String(d.dataDocumento).slice(0, 10),
              sedeOperativa: d.sedeOperativa || '',
              oggetto: d.oggetto || '', causale: d.causale || '',
              note: d.note || '',
            });
            setRighe(rr.length ? rr.map(r => ({
              prodottoId: r.prodottoId, codice: r.codice || '',
              descrizione: r.descrizione, dettaglio: r.dettaglio || '',
              udm: r.udm || '', quantita: parseFloat(r.quantita),
              prezzoUnitario: parseFloat(r.prezzoUnitario),
              scontoPerc: parseFloat(r.scontoPerc),
              aliquotaIva: parseFloat(r.aliquotaIva),
              soggettaRitenuta: !!r.soggettaRitenuta,
            })) : [{ ...RIGA_VUOTA }]);
          }
        }
      } catch (e) {
        console.error(e);
      } finally { setLoading(false); }
    })();
  }, [id]);

  // sedi del cliente selezionato
  useEffect(() => {
    if (!form.clienteId) { setSedi([]); return; }
    api.get(`/documenti/sedi/${form.clienteId}`)
      .then(r => setSedi(r.data?.data || []))
      .catch(() => setSedi([]));
  }, [form.clienteId]);

  // ricerca catalogo
  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/documenti/catalogo?q=${encodeURIComponent(cercaProd)}`)
        .then(r => setCatalogo(r.data?.data || []))
        .catch(() => setCatalogo([]));
    }, 300);
    return () => clearTimeout(t);
  }, [cercaProd]);

  const aggiornaRiga = (i, campo, valore) =>
    setRighe(rs => rs.map((r, k) => k === i ? { ...r, [campo]: valore } : r));

  const aggiungiDaCatalogo = (p) => {
    setRighe(rs => {
      const nuove = [...rs];
      const vuota = nuove.findIndex(r => !r.descrizione.trim());
      const riga = {
        ...RIGA_VUOTA,
        prodottoId: p.id, codice: p.codice || '',
        descrizione: p.nome, dettaglio: p.descrizione || '',
        udm: p.udm || '', quantita: 1,
        prezzoUnitario: parseFloat(p.prezzoNetto || 0),
        aliquotaIva: parseFloat(p.aliquotaIva ?? 0.22),
        soggettaRitenuta: !!p.soggettoRitenuta,
      };
      if (vuota >= 0) nuove[vuota] = riga; else nuove.push(riga);
      return nuove;
    });
  };

  const importoRiga = r =>
    (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzoUnitario) || 0) *
    (1 - (parseFloat(r.scontoPerc) || 0) / 100);

  const imponibile = righe.reduce((s, r) => s + importoRiga(r), 0);
  const iva = righe.reduce((s, r) =>
    s + importoRiga(r) * (parseFloat(r.aliquotaIva) || 0), 0);

  const salva = async () => {
    if (!form.clienteId) { window.alert('Scegli il cliente.'); return; }
    const valide = righe.filter(r => r.descrizione.trim());
    if (valide.length === 0) { window.alert('Aggiungi almeno una riga.'); return; }

    setBusy(true);
    try {
      const body = { ...form, tipo: tipoDoc, clienteId: parseInt(form.clienteId, 10),
                     righe: valide };
      const r = id
        ? await api.put(`/documenti/${id}`, body)
        : await api.post('/documenti', body);
      onSalvato(id || r.data?.id);
    } catch (e) {
      window.alert('Errore: ' + (e?.response?.data?.error || e.message));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  const inp = { fontSize: 13, padding: '4px 6px', border: '1px solid #e5e7eb',
                borderRadius: 6, width: '100%' };

  return (
    <div className="page-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="btn btn-secondary" onClick={onBack}>Annulla</button>
        <h2 style={{ margin: 0, fontSize: 18 }}>
          {id ? 'Modifica' : 'Nuovo'} {tipoDoc === 'ddt' ? 'DDT' : 'preventivo'}
        </h2>
        <button className="btn btn-primary" style={{ marginLeft: 'auto' }}
          onClick={salva} disabled={busy}>
          {busy ? 'Salvataggio...' : 'Salva bozza'}
        </button>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12,
                      marginBottom: 12 }}>
          <div>
            <label className="form-label">Cliente *</label>
            <select className="form-control" value={form.clienteId}
              onChange={e => setForm(f => ({ ...f, clienteId: e.target.value }))}>
              <option value="">&mdash; scegli &mdash;</option>
              {clienti.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Data</label>
            <input type="date" className="form-control" value={form.dataDocumento}
              onChange={e => setForm(f => ({ ...f, dataDocumento: e.target.value }))} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label className="form-label">Sede operativa</label>
            {sedi.length > 0 ? (
              <select className="form-control" value={form.sedeOperativa}
                onChange={e => setForm(f => ({ ...f, sedeOperativa: e.target.value }))}>
                <option value="">&mdash; nessuna &mdash;</option>
                {sedi.map((s, i) => (
                  <option key={i} value={s.indirizzo}>{s.indirizzo}</option>
                ))}
              </select>
            ) : (
              <input className="form-control" value={form.sedeOperativa}
                onChange={e => setForm(f => ({ ...f, sedeOperativa: e.target.value }))}
                placeholder="Nessuna sede sui contratti" />
            )}
          </div>
          <div>
            <label className="form-label">Oggetto</label>
            <input className="form-control" value={form.oggetto}
              onChange={e => setForm(f => ({ ...f, oggetto: e.target.value }))} />
          </div>
        </div>

        {tipoDoc === 'ddt' && (
          <div style={{ marginTop: 12 }}>
            <label className="form-label">Causale del trasporto</label>
            <input className="form-control" value={form.causale}
              onChange={e => setForm(f => ({ ...f, causale: e.target.value }))}
              placeholder="Se vuoto usa quella predefinita" />
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
        <div className="card" style={{ padding: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Righe</div>
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead><tr style={{ fontSize: 11, color: '#6b7280', textAlign: 'left' }}>
              <th style={{ width: 80 }}>Codice</th>
              <th>Descrizione</th>
              <th style={{ width: 60 }}>Q.ta</th>
              <th style={{ width: 80 }}>Prezzo</th>
              <th style={{ width: 50 }}>Sc.%</th>
              <th style={{ width: 40, textAlign: 'center' }}>R.A.</th>
              <th style={{ width: 90, textAlign: 'right' }}>Importo</th>
              <th style={{ width: 30 }}></th>
            </tr></thead>
            <tbody>
              {righe.map((r, i) => (
                <tr key={i}>
                  <td><input style={inp} value={r.codice}
                        onChange={e => aggiornaRiga(i, 'codice', e.target.value)} /></td>
                  <td><input style={inp} value={r.descrizione}
                        onChange={e => aggiornaRiga(i, 'descrizione', e.target.value)} /></td>
                  <td><input style={inp} type="number" step="0.01" value={r.quantita}
                        onChange={e => aggiornaRiga(i, 'quantita', e.target.value)} /></td>
                  <td><input style={inp} type="number" step="0.01" value={r.prezzoUnitario}
                        onChange={e => aggiornaRiga(i, 'prezzoUnitario', e.target.value)} /></td>
                  <td><input style={inp} type="number" step="1" value={r.scontoPerc}
                        onChange={e => aggiornaRiga(i, 'scontoPerc', e.target.value)} /></td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={r.soggettaRitenuta}
                      onChange={e => aggiornaRiga(i, 'soggettaRitenuta', e.target.checked)} />
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {eur(importoRiga(r))}
                  </td>
                  <td>
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => setRighe(rs => rs.length > 1
                        ? rs.filter((_, k) => k !== i) : [{ ...RIGA_VUOTA }])}>
                      &times;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }}
            onClick={() => setRighe(rs => [...rs, { ...RIGA_VUOTA }])}>
            Aggiungi riga
          </button>

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <table style={{ fontSize: 13 }}>
              <tbody>
                <tr><td style={{ padding: '2px 16px 2px 0' }}>Imponibile</td>
                    <td style={{ textAlign: 'right' }}>{eur(imponibile)}</td></tr>
                <tr><td style={{ padding: '2px 16px 2px 0' }}>IVA</td>
                    <td style={{ textAlign: 'right' }}>{eur(iva)}</td></tr>
                <tr><td style={{ padding: '6px 16px 2px 0', fontWeight: 700,
                                 borderTop: '1px solid #e5e7eb' }}>TOTALE</td>
                    <td style={{ textAlign: 'right', fontWeight: 700,
                                 borderTop: '1px solid #e5e7eb' }}>
                      {eur(imponibile + iva)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="card" style={{ padding: 12, alignSelf: 'start' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Catalogo</div>
          <input className="form-control" style={{ fontSize: 13, marginBottom: 8 }}
            placeholder="Cerca articolo..."
            value={cercaProd} onChange={e => setCercaProd(e.target.value)} />
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {catalogo.map(p => (
              <div key={p.id} onClick={() => aggiungiDaCatalogo(p)}
                style={{ padding: '6px 4px', borderBottom: '1px solid #f1f5f9',
                         cursor: 'pointer', fontSize: 12 }}>
                <div style={{ fontWeight: 600 }}>{p.nome}</div>
                <div style={{ color: '#9ca3af' }}>
                  {p.codice || ''} {p.prezzoNetto ? '\u00b7 ' + eur(p.prezzoNetto) : ''}
                  {p.soggettoRitenuta ? ' \u00b7 R.A.' : ''}
                </div>
              </div>
            ))}
            {catalogo.length === 0 && (
              <div style={{ color: '#9ca3af', fontSize: 12, padding: 8 }}>
                Nessun articolo
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
